import { EBeneficiaryType, ECreditApplicationStatus, EDecision, EFinancingProgram } from '@if/core/types'
import { PageContainer } from '@if/mui'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import {
  useCreditApplicationById,
  useCreditApplicationByIdQuery,
  useEditCreditApplication,
} from '@src/data/api/credit-api/credit-api'
import { DraftCreditApplicationDto } from '@src/types/CreditApplicationSchema'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  adjustCreditApplicationYearsForApi,
  adjustCreditApplicationYearsFromApi,
  mustAskApplicantSIN,
  mustAskCoapplicantSIN,
} from '../ViewCreditApplicationPage/credit-hooks'
import ApplicationForm from './components/ApplicationForm'

interface Props {
  id: string
  financingProgramId: EFinancingProgram
}

const EditCreditApplicationBody = ({ id, financingProgramId }: Props): JSX.Element => {
  const merchant = useMerchant()
  const navigate = useNavigate()

  const [creditApplication, isLoadingCreditApp] = useCreditApplicationById(
    { creditApplicationId: id, financingProgramId },
    false,
  ) // if true, poll every 5 secs

  const shouldShowSimplifiedApplicantEditDialog =
    creditApplication?.applicant?.incomeVerification?.allIncomeTasksConfirmed &&
    !creditApplication?.applicant?.incomeVerification?.hasSkippedIncomeValidation
  const shouldShowSimplifiedCoApplicantEditDialog =
    creditApplication?.coApplicant?.incomeVerification?.allIncomeTasksConfirmed &&
    !creditApplication?.coApplicant?.incomeVerification?.hasSkippedIncomeValidation

  const [editCreditAppMutation, , , reset] = useEditCreditApplication()
  const onSave = async (data: Partial<DraftCreditApplicationDto>) => {
    data.id = id
    if (!data.beneficiaryTypeId) data.beneficiaryTypeId = EBeneficiaryType.Applicant
    adjustCreditApplicationYearsForApi(data)
    await editCreditAppMutation(data).then(() => {
      reset()
      if (data.status === ECreditApplicationStatus.Draft) navigate('/credits')
      else navigate(`/credits/${data.financingProgramId}/${data.id}`)
    })
  }
  const isPrequalComplete =
    creditApplication?.prequalificationDecision?.decision === EDecision.Approved ||
    (creditApplication?.prequalificationDecision?.decision === EDecision.Refused &&
      creditApplication?.applicant?.hasCreditReport === true)

  return (
    <>
      {!isLoadingCreditApp && creditApplication && (
        <ApplicationForm
          mode="edit"
          merchant={merchant}
          creditApplication={adjustCreditApplicationYearsFromApi(creditApplication)}
          onSave={onSave}
          financingProgramId={financingProgramId}
          lockApplicantIncomeAndExpenses={shouldShowSimplifiedApplicantEditDialog}
          lockCoApplicantIncomeAndExpenses={shouldShowSimplifiedCoApplicantEditDialog}
          isPrequalComplete={isPrequalComplete}
          isApplicantSinNeeded={mustAskApplicantSIN(creditApplication)}
          isCoapplicantSinNeeded={mustAskCoapplicantSIN(creditApplication)}
        />
      )}
    </>
  )
}

const editCreditAppParams = ['financingProgramId', 'id'] as const

const useEditCreditApplicationPrimary = ({ financingProgramId, id }: { financingProgramId: string; id: string }) => ({
  creditApplication: useCreditApplicationByIdQuery(
    { creditApplicationId: id, financingProgramId: financingProgramId as EFinancingProgram },
    false,
  ),
})

const EditCreditApplicationPage = () => {
  const { t } = useTranslation()
  return (
    <PageContainer
      params={editCreditAppParams}
      usePrimary={useEditCreditApplicationPrimary}
      title={t('newApplication.editTitle')}
      subtitle={t('newApplication.editSubtitle')}
    >
      {({ creditApplication }) => (
        <EditCreditApplicationBody
          id={creditApplication.id}
          financingProgramId={creditApplication.financingProgramId}
        />
      )}
    </PageContainer>
  )
}

export default EditCreditApplicationPage
